import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { ShoppingCart, Menu, X, Instagram, Facebook } from 'lucide-react';

const HERO_BACKGROUNDS = {
  '/': "https://customer-assets.emergentagent.com/job_c40d2a50-2ea7-48bd-927c-1b6d899d3de9/artifacts/lvxh91xj_Round%20Shape%20Traditional%20Indian%20Wood%20Block%20Pattern%20for%20Textile%20Printing%20on%20Rustic%20Wood%20Background.%20Block%20Printing%2CRajasthan%20India%20Block%20Printing%2CWood%20Block%20Used%20for%20Handmade%20Textile%20Printing%2CHand%20Craft.jpg",
  '/about': "https://customer-assets.emergentagent.com/job_c2e293f8-1738-4ed6-a581-e197b34b7fa1/artifacts/q6qvxuog_Wood%2C%20Plastic%20Panels%2C%20High-Precision%20CNC%20Laser%20Cutting%2C%20High-Speed%20Cutting%2C%20Laser%20Welding%2C%20Laser%20Cutting%20Technology%2C%20Laser%20Welding..jpg",
  '/contact': "https://customer-assets.emergentagent.com/job_c2e293f8-1738-4ed6-a581-e197b34b7fa1/artifacts/67152p73_ChatGPT%20Image%20Apr%209%2C%202026%2C%2009_12_11%20AM.png",
};

const DEFAULT_BG = HERO_BACKGROUNDS['/'];

const getHeroBg = (pathname) => HERO_BACKGROUNDS[pathname] || DEFAULT_BG;

const Navbar = () => {
  const location = useLocation();
  const { cartCount } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const heroBg = getHeroBg(location.pathname);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/woodwork-and-art', label: 'Woodwork & Art' },
    { path: '/mandirs', label: 'Bespoke Mandirs' },
    { path: '/copperware', label: 'Artisan Metals' },
    { path: '/candles', label: 'Ayurvedic Aromas' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' },
    { path: '/gallery', label: 'Gallery' },
  ];

  return (
    <nav className="relative sticky top-0 z-50" data-testid="navbar">
      <div className="absolute inset-0 bg-cover bg-center bg-no-repeat -z-10" style={{ backgroundImage: `url('${heroBg}')` }} />
      <div className="absolute inset-0 bg-black/60 -z-10" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_5652a5b2-0bf3-4ac0-b2a9-e954b340d44d/artifacts/2zrootit_ChatGPT_Image_Apr_10__2026__11_26_27_PM-removebg-preview.png" 
              alt="Rasaa Designs Co Logo" 
              className="h-[4.5rem] w-auto"
            />
            <div className="flex flex-col leading-none whitespace-nowrap">
              <span className="text-xl font-bold text-white tracking-wider" style={{ fontFamily: 'Playfair Display, serif' }}>
                RASAA ~
              </span>
              <span className="text-sm font-bold text-white tracking-[0.25em] mt-0.5" style={{ fontFamily: 'Playfair Display, serif' }}>
                THE ESSENCE
              </span>
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-5 ml-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm tracking-wide transition-colors duration-300 whitespace-nowrap px-1 ${
                  location.pathname === link.path ? 'text-[#D4A843] font-medium' : 'text-white/90 hover:text-[#D4A843]'
                }`}
                data-testid={`nav-link-${link.path.slice(1)}`}
              >
                {link.label}
              </Link>
            ))}
            
            <div className="flex items-center gap-3 border-l border-white/20 pl-4">
              <a href="https://www.instagram.com/rasaa.designs" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-[#D4A843] transition-colors" data-testid="instagram-link">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://www.facebook.com/RASAATheEssence" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-[#D4A843] transition-colors" data-testid="facebook-link">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://pinterest.com" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-[#D4A843] transition-colors" data-testid="pinterest-link">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm0 19c-.721 0-1.418-.109-2.073-.312.286-.465.713-1.227.87-1.835l.437-1.664c.229.436.895.804 1.604.804 2.111 0 3.633-1.941 3.633-4.354 0-2.312-1.888-4.042-4.316-4.042-3.021 0-4.625 2.003-4.625 4.137 0 1.095.424 2.068 1.333 2.433.14.056.215.031.248-.085l.209-.857c.011-.046.005-.088-.032-.126-.282-.344-.463-.789-.463-1.421 0-1.377 1.037-2.714 2.809-2.714 1.525 0 2.588 1.037 2.588 2.528 0 1.667-.831 2.812-1.914 2.812-.607 0-1.062-.502-.916-1.118.174-.735.512-1.529.512-2.059 0-.475-.255-.871-.783-.871-.621 0-1.119.642-1.119 1.502 0 .548.185.919.185.919l-.753 3.192c-.149.632-.088 1.507-.019 2.101-3.349-1.471-5.683-4.818-5.683-8.729 0-5.239 4.247-9.486 9.486-9.486s9.486 4.247 9.486 9.486c0 5.238-4.247 9.486-9.486 9.486z"/>
                </svg>
              </a>
            </div>
            
            <Link to="/cart" className="relative" data-testid="cart-link">
              <ShoppingCart className="w-5 h-5 text-white/80 hover:text-[#D4A843] transition-colors" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#D4A843] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center" data-testid="cart-count">
                  {cartCount}
                </span>
              )}
            </Link>
          </div>

          <button
            className="lg:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden pb-4" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="block py-2 text-white/90 hover:text-[#D4A843]"
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`mobile-nav-link-${link.path.slice(1)}`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/cart"
              className="block py-2 text-white/90 hover:text-[#D4A843]"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-cart-link"
            >
              Cart ({cartCount})
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
